import Select, { ClassNamesConfig, MenuPlacement, MenuPosition } from "react-select";
import CreatableSelect from "react-select/creatable";
import React, { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { SelectComponentsConfig } from "react-select/dist/declarations/src/components";
import { ClearIndicator, NoOptionsMessage, OptionWithDescription, selectStyles } from "../config";
import { useSharedTranslations } from "../../../lib";
import styles from "../react-select.module.scss";

export interface OptionType {
    label: string;
    value: string;

    [key: string]: any;
}

interface ISearchableSelectProps {
    id?: string;
    className?: string;
    menuPlacement?: MenuPlacement;
    menuPosition?: MenuPosition;
    classNames?: ClassNamesConfig;
    value?: string | string[] | null;
    optionValue?: OptionType | OptionType[] | null;
    internalOptionsValue?: OptionType[] | null;
    emptyByDefault?: boolean;
    validate?: boolean;
    triggerValidate?: number;
    validateMessage?: string;
    errorMessage?: string;
    withTooltip?: boolean;
    disabled?: boolean;
    loading?: boolean;
    placeholder?: string;
    options: OptionType[];
    isSearchable?: boolean;
    isMulti?: boolean;
    hideClearIcon?: boolean;
    isClearable?: boolean;
    isCreatable?: boolean;
    isValidNewOption?: (inputValue: string) => boolean;
    onInputChange?: (inputValue: string) => void;
    name?: string;
    onChange?: (value: string | string[] | null) => void;
    formatOptionLabel?: any;
    onOptionChange?: (value: OptionType | OptionType[] | null) => void;
    onClear?: () => void;
    components?: SelectComponentsConfig<any, any, any>;
    portalTarget?: HTMLElement,
    defaultMenuIsOpen?: boolean,
    menuIsOpen?: boolean,
    closeMenuOnSelect?: boolean,
    onMenuClose?: () => void;
    displayValueInSelect?: boolean;
}

export const SearchableSelect: React.FC<ISearchableSelectProps> = ({
                                                                       id,
                                                                       className,
                                                                       value,
                                                                       validate,
                                                                       triggerValidate,
                                                                       validateMessage,
                                                                       optionValue,
                                                                       internalOptionsValue,
                                                                       formatOptionLabel,
                                                                       options,
                                                                       emptyByDefault,
                                                                       withTooltip,
                                                                       name,
                                                                       placeholder = "",
                                                                       loading = false,
                                                                       disabled = false,
                                                                       isMulti = false,
                                                                       isSearchable = true,
                                                                       isCreatable = false,
                                                                       hideClearIcon = false,
                                                                       isClearable = true,
                                                                       displayValueInSelect = true,
                                                                       menuPlacement,
                                                                       menuPosition,
                                                                       onChange,
                                                                       onOptionChange,
                                                                       onClear,
                                                                       portalTarget,
                                                                       ...props
                                                                   }) => {
    const [selectValue, setSelectValue] = useState<OptionType | OptionType[]>();
    const [isMenuOpened, setIsMenuOpened] = useState(false);
    const [isError, setIsError] = useState(false);
    const [validationError, setValidationError] = useState<string>();
    const t = useSharedTranslations();
    const { i18n } = useTranslation();
    const [width, setWidth] = useState<number>(window.innerWidth);
    const isDesktop = useMemo(() => {
        return width > 450;
    }, [width]);

    const selectMenuPlacement = useMemo(() => {
        if (menuPlacement) {
            return menuPlacement;
        } else {
            return isDesktop ? "auto" : "bottom";
        }
    }, [isDesktop, menuPlacement]);

    const selectMenuPosition = useMemo(() => {
        if (menuPosition) {
            return menuPosition;
        } else {
            return isDesktop ? "fixed" : "absolute";
        }
    }, [isDesktop, menuPosition]);

    const onWindowResize = () => {
        setWidth(window.innerWidth);
    };

    useEffect(() => {
        window.addEventListener("resize", onWindowResize);

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, []);

    useEffect(() => {
        if (triggerValidate && validate) {
            validateValue(selectValue);
        } else {
            setIsError(false);
            setValidationError(undefined);
        }
    }, [selectValue, triggerValidate, validate]);

    useEffect(() => {
        if (isError) {
            validateValue(selectValue);
        }
    }, [i18n.language]);

    const transformValueToOption = (value: string): OptionType => {
        return {
            label: value,
            value: value
        };
    };

    const getOptionsValue = (value: string): OptionType | undefined => {
        return options.filter((option) => option.value === value)[0];
    };

    const validateValue = (val: OptionType | OptionType[] | undefined) => {
        let error = false;
        let message: string | undefined = undefined;
        if (internalOptionsValue ? internalOptionsValue.length === 0 : (!val || (Array.isArray(val) && val.length === 0))) {
            error = true;
            message = validateMessage ? validateMessage : t("requiredFieldError");
        }
        setIsError(error);
        setValidationError(message);
    };

    useEffect(() => {
        if (emptyByDefault && !value) {
            if (isMulti) {
                setSelectValue([]);
            }
            return;
        }
        if (isMulti) {
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    setSelectValue(value.map((val) => {
                        if (getOptionsValue(val)) {
                            return getOptionsValue(val) as OptionType;
                        } else {
                            return transformValueToOption(val);
                        }
                    }));
                    setSelectValue(value.map((val) => transformValueToOption(val)));
                } else {
                    setSelectValue([]);
                }
            }
        } else {
            if (value && !Array.isArray(value)) {
                setSelectValue(getOptionsValue(value) ? getOptionsValue(value) : transformValueToOption(value));
            } else {
                setSelectValue([]);
            }
        }
        if (optionValue && !value) {
            setSelectValue(optionValue);
        }
    }, [optionValue, value, options, isMulti]);

    function handleChange(val: any) {
        if (isMulti) {
            if (Array.isArray(val) && val.length > 0) {
                onChange && onChange(val.map((v) => v.value));
            } else {
                onChange && onChange(null);
            }
        } else {
            if (val && !Array.isArray(val)) {
                onChange && onChange(val.value);
            } else {
                onChange && onChange(null);
            }
        }
        onOptionChange && onOptionChange(val);
        if (validate) {
            validateValue(val);
        }
        displayValueInSelect && setSelectValue(val);
    }

    const onKeyDown = (e: React.KeyboardEvent<any>) => {
        if (isMenuOpened) {
            e.stopPropagation();
        }
    };

    const components: any = {
        NoOptionsMessage
    };

    if (hideClearIcon || !isClearable) {
        components["ClearIndicator"] = ClearIndicator;
    }

    if (withTooltip) {
        components["Option"] = OptionWithDescription;
    }

    const displayedError = props.errorMessage || (isError ? validationError : undefined);
    const errorId = id ? `${id}-error` : undefined;
    const fieldStyles: typeof selectStyles = {
        ...selectStyles,
        control: (base, state) => {
            const control = selectStyles.control ? selectStyles.control(base, state) : base;
            return displayedError ? {
                ...control,
                borderColor: "var(--hr-color-special-error)",
                ":hover": { borderColor: "var(--hr-color-special-error)" }
            } : control;
        }
    };
    const selectProps = {
        id: id,
        className: cn(styles.reactSelect, displayedError ? styles.reactSelectError : undefined, disabled ? styles.reactSelectDisabled : undefined),
        "aria-invalid": Boolean(displayedError),
        "aria-errormessage": displayedError ? errorId : undefined,
        classNames: props.classNames,
        components: {
            ...components,
            ...props.components
        },
        formatOptionLabel: formatOptionLabel,
        isLoading: loading,
        placeholder: placeholder,
        styles: fieldStyles,
        value: selectValue,
        isDisabled: disabled,
        isClearable: isClearable,
        isSearchable: isSearchable,
        onKeyDown: onKeyDown,
        onMenuOpen: () => setIsMenuOpened(true),
        onMenuClose: () => {
            if (props.onMenuClose) {
                props.onMenuClose();
            }
            setIsMenuOpened(false);
        },
        formatCreateLabel: (input: string) => {
            return `${t("create")} "${input}"`;
        },
        isMulti: isMulti,
        closeMenuOnSelect: props.closeMenuOnSelect !== undefined ? props.closeMenuOnSelect : !isMulti,
        name: name,
        options: options,
        onChange: handleChange,
        onInputChange: props.onInputChange,
        isValidNewOption: props.isValidNewOption,
        menuPlacement: selectMenuPlacement,
        menuPosition: selectMenuPosition,
        menuPortalTarget: portalTarget,
        onClear: onClear,
        defaultMenuIsOpen: props.defaultMenuIsOpen,
        menuIsOpen: props.menuIsOpen,
        maxMenuHeight: !isDesktop ? 110 : undefined
    };

    return <div className={className}>
        {isCreatable ? <CreatableSelect {...selectProps}/> : <Select {...selectProps}/>}
        {displayedError && <div className="Textinput_state_error">
            <div id={errorId} role="alert" className={cn(styles.validationError, "Textinput-Hint")}>
                {displayedError}
            </div>
        </div>}
    </div>;

};
